import { maxSlotGapMs } from './schedule.js';
/** Name of the plan every new organization starts with, by UI language. */
export const STANDARD_PLAN_NAME = { en: 'Standard backup', he: 'גיבוי רגיל' };
/**
 * Every new organization's default plan: personal folders of every user, several times during the day,
 * about three months of history, and a warning after three days without a backup (weekends stay quiet).
 */
export const STANDARD_PLAN_SPEC = {
    selection: { mode: 'profiles', profileFolders: ['Documents', 'Desktop', 'Pictures'], profiles: 'all' },
    schedule: { kind: 'interval', everyMinutes: 180, windowStart: '08:00', windowEnd: '21:00', timezone: 'device', jitterMinutes: 15, catchUp: 'once' },
    retention: { intradayDays: 7, daily: 30, weekly: 13, monthly: 0, yearly: 0, keepLast: 3 },
    alerting: { maxAgeHours: 72 },
};
export const PERSONAL_FOLDERS = ['Documents', 'Desktop', 'Pictures', 'Downloads', 'Music', 'Videos'];
const SEVERAL_DAILY = { everyMinutes: 180, windowStart: '08:00', windowEnd: '21:00' };
const everyDay = (days) => !days || days.length === 0 || days.length === 7;
/** Which recommended choice a schedule corresponds to ("custom" when none fits exactly). */
export function matchSchedulePreset(s) {
    if (s.kind === 'interval' && s.everyMinutes === SEVERAL_DAILY.everyMinutes && s.windowStart === SEVERAL_DAILY.windowStart && s.windowEnd === SEVERAL_DAILY.windowEnd && everyDay(s.days))
        return 'several-daily';
    if (s.kind === 'daily' && s.times?.length === 1 && everyDay(s.days))
        return 'daily';
    if (s.kind === 'weekly' && s.times?.length === 1 && s.days?.length === 1)
        return 'weekly';
    return 'custom';
}
/**
 * The schedule for a recommended choice. Advanced fields (time zone, start spreading, catch-up) are kept
 * from `base`. `time` is "HH:MM" for daily/weekly; `day` is 0=Sunday..6=Saturday for weekly.
 */
export function scheduleForPreset(preset, base, opts = {}) {
    const keep = { timezone: base.timezone, jitterMinutes: base.jitterMinutes, catchUp: base.catchUp };
    if (preset === 'several-daily')
        return { kind: 'interval', ...SEVERAL_DAILY, ...keep };
    const time = opts.time ?? base.times?.[0] ?? '12:00';
    if (preset === 'daily')
        return { kind: 'daily', times: [time], ...keep };
    return { kind: 'weekly', times: [time], days: [opts.day ?? base.days?.[0] ?? 1], ...keep };
}
/** Choices offered for "warn me if a computer goes without a backup for …", in hours. */
export const ALERT_AFTER_CHOICES = [24, 48, 72, 168, 192];
/**
 * A sensible "warn after" for a schedule: at least three days (so computers switched off over a weekend
 * don't raise alarms) and at least a day longer than the longest normal gap between backups.
 */
export function suggestedMaxAgeHours(s, fromMs = Date.now()) {
    const tz = s.timezone === 'device' ? 'UTC' : s.timezone;
    const gapHours = Math.ceil(maxSlotGapMs(s, tz, fromMs) / 3_600_000);
    const want = Math.max(72, gapHours + 24);
    return ALERT_AFTER_CHOICES.find((h) => h >= want) ?? want;
}
export const HISTORY_PRESETS = {
    '1m': { intradayDays: 7, daily: 30, weekly: 0, monthly: 0, yearly: 0, keepLast: 3 },
    '3m': { intradayDays: 7, daily: 30, weekly: 13, monthly: 0, yearly: 0, keepLast: 3 },
    '1y': { intradayDays: 7, daily: 30, weekly: 13, monthly: 12, yearly: 0, keepLast: 3 },
    '7y': { intradayDays: 7, daily: 30, weekly: 13, monthly: 12, yearly: 7, keepLast: 3 },
};
const RETENTION_KEYS = ['intradayDays', 'daily', 'weekly', 'monthly', 'yearly', 'keepLast'];
export function matchHistoryPreset(r) {
    for (const [key, p] of Object.entries(HISTORY_PRESETS))
        if (RETENTION_KEYS.every((k) => p[k] === r[k]))
            return key;
    return 'custom';
}
/** Roughly how many days back a retention setting lets you go. Used to warn before history gets shorter. */
export function historyDays(r) {
    return Math.max(r.intradayDays, r.daily, r.weekly * 7, r.monthly * 31, r.yearly * 366);
}
// ───────── what to back up ─────────
/** The platform a folder path belongs to, inferred from its shape (undefined = both). */
export function pathPlatform(path) {
    const p = path.trim();
    if (/^[A-Za-z]:[\\/]/.test(p) || p.startsWith('\\\\') || /^%[A-Za-z_]\w*%/.test(p))
        return 'windows';
    if (p.startsWith('/') || /^\$\{?[A-Za-z_]\w*\}?/.test(p))
        return 'linux';
    return undefined;
}
/**
 * The selection for "these personal folders of every user, plus these other folders". Personal folders
 * switch the plan to per-user mode; with none ticked only the listed folders are backed up. Exclusions and
 * other filters are kept from `base`.
 */
export function buildSelection(base, personal, otherFolders) {
    const includePaths = otherFolders
        .map((p) => p.trim())
        .filter(Boolean)
        .map((path) => {
        const os = pathPlatform(path);
        return os ? { path, os } : { path };
    });
    return { ...base, mode: personal.length > 0 ? 'profiles' : 'paths', profileFolders: [...personal], includePaths };
}
/** Video and music files, skipped by the "skip videos and music" choice. */
export const MEDIA_EXTENSIONS = ['mp4', 'mov', 'avi', 'mkv', 'wmv', 'm4v', 'mp3', 'wav', 'flac', 'm4a', 'aac', 'wma'];
/** Temporary and system clutter, skipped by the "skip temporary files" choice. */
export const TEMP_FILE_PATTERNS = ['*.tmp', '*.temp', '~$*', 'Thumbs.db', 'desktop.ini', '.DS_Store'];
export const includesAll = (list, items) => items.every((i) => list.some((x) => x.toLowerCase() === i.toLowerCase()));
/** Add or remove a whole group of values (case-insensitive) from a list. */
export function toggleAll(list, items, on) {
    const rest = list.filter((x) => !items.some((i) => i.toLowerCase() === x.toLowerCase()));
    return on ? [...rest, ...items] : rest;
}
// ───────── phones ─────────
/**
 * Phones are deliberately not configurable (docs/mobile.md, decision M4). Every phone in an organization
 * runs this one built-in plan: back up the app's own "My Files" folder, continuously, and keep a deleted
 * file recoverable for the organization's window. The only lever is that window.
 */
export const MOBILE_PLAN_NAME = { en: 'Phone files', he: 'קבצים מהנייד' };
/** How long a file deleted on a phone stays recoverable. The tenant's plan caps the maximum. */
export const MOBILE_RETENTION_DAYS = [30, 90, 365];
export const DEFAULT_MOBILE_RETENTION_DAYS = 30;
export function isMobileRetentionDays(n) {
    return MOBILE_RETENTION_DAYS.includes(n);
}
/** Normalise whatever is stored in tenant settings into a supported window. */
export function mobileRetentionDays(raw, cap = 365) {
    const n = isMobileRetentionDays(raw) ? raw : DEFAULT_MOBILE_RETENTION_DAYS;
    const allowed = MOBILE_RETENTION_DAYS.filter((d) => d <= cap);
    return n <= cap ? n : (allowed[allowed.length - 1] ?? DEFAULT_MOBILE_RETENTION_DAYS);
}
/**
 * `daily: <window>` keeps the newest recovery point of each of the last <window> days that have any, so a
 * file deleted today stays reachable through the previous day's point for exactly that many days. keepLast
 * protects the newest few points on a phone that has been quiet.
 */
export function mobilePlanSpec(days = DEFAULT_MOBILE_RETENTION_DAYS) {
    return {
        selection: { mode: 'mobile' },
        schedule: {
            kind: 'continuous',
            debounceSeconds: 10,
            maxDelayMinutes: 60,
            safetyNetMinutes: 360,
            timezone: 'device',
            jitterMinutes: 0,
            catchUp: 'none',
        },
        retention: { intradayDays: 0, daily: days, weekly: 0, monthly: 0, yearly: 0, keepLast: 5 },
        limits: {
            uploadConcurrency: 2,
            bandwidthKbps: 0,
            cpuPriority: 'low',
            maxDurationMinutes: 60,
            // A phone is always on battery, and a shared file that waits for Wi-Fi is a broken promise. The app
            // offers "back up over mobile data" as a device setting instead of a plan field.
            onBattery: 'run',
            meteredNetwork: 'run',
            maxMemoryMB: 256,
            cacheMaxMB: 512,
        },
        vss: 'off',
        // Restore drills target computers; a phone is never a drill destination.
        verification: { sampleEveryDays: 7, sampleChunks: 32, restoreDrillEveryDays: 0 },
        alerting: { maxAgeHours: 24 },
        retry: { maxRetries: 5, retryDelayMinutes: 5 },
    };
}
//# sourceMappingURL=plan-presets.js.map